/**
 * Document TTS FSM store — Frontend FSM Pattern (R-A1..R-A5).
 *
 * Manages state for reading a Markdown document aloud via the pull-model TTS
 * protocol (TTS_SEGMENT_SYNTH / TTS_SEGMENT_AUDIO / TTS_SEGMENT_ERROR — wire
 * values are still 'tts_doc_*', frozen for compat; see WsEvent in shared/).
 *
 * The frontend is the single owner of segments:
 *   - After MarkdownContent renders with ttsMode=true, it walks the DOM to
 *     extract [{ index, ttsText, skip }] and calls setSegments().
 *   - To play segment N, the store dispatches TTS_SEGMENT_SYNTH via a provided
 *     emit function. The audio response updates this store.
 *
 * ── Arm-then-start (fixes the original chicken-and-egg deadlock) ────────────
 *   startReading() no longer requires segments to already exist. Clicking
 *   "Read aloud" ARMS the machine (phase → 'loading'), which flips ttsMode on
 *   in CodeViewer → MarkdownContent annotates → setSegments() arrives → the
 *   first synth request fires. Previously startReading() bailed when
 *   segments.length === 0, but segments only populate once ttsMode (= phase
 *   !== idle) is true — so phase never left idle and nothing ever happened.
 *
 * ── Audio unlock (mobile autoplay) ─────────────────────────────────────────
 *   A SINGLE HTMLAudioElement is reused for every segment and is "unlocked"
 *   inside the user's click gesture via unlockAudio(). Mobile browsers block
 *   programmatic play() that isn't tied to a gesture; the first segment plays
 *   only AFTER the WS round-trip (outside the gesture), so without priming the
 *   element the play() would be rejected and swallowed → "nothing happens".
 */
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { nextPlaybackRate } from './playbackRate';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DocTtsPhase = 'idle' | 'loading' | 'playing' | 'paused' | 'stopped';

/**
 * What this read-aloud session is reading. Discriminated union so the same
 * singleton store can drive either the markdown file viewer or (P1) an
 * individual chat message — one audio element = one audible read, the source
 * just records *where* it came from.
 *
 * - `doc`         — a markdown file in the CodeViewer (the only P0 caller).
 * - `chatMessage` — SEAM for P1 chat-message read-aloud. Defined now so the
 *   field shape is stable, but UNUSED in P0 (no caller constructs it yet).
 */
export type TtsSource =
  | { kind: 'doc'; fileId: string }
  | { kind: 'chatMessage'; sessionId: string; messageId: string };

export interface TtsSegment {
  index: number;
  ttsText: string;   // extracted from element.textContent (markdown already stripped by browser)
  skip: boolean;     // true for code blocks — don't request synthesis
}

interface QueuedAudio {
  index: number;
  audioBase64: string;
  durationMs: number;
}

interface DocTtsState {
  phase: DocTtsPhase;
  source: TtsSource | null;
  segments: TtsSegment[];
  currentSegment: number;
  pendingAudio: Map<number, QueuedAudio>;
  playbackRate: 1 | 1.5 | 2;
  // Auto-center the viewport on the currently-playing paragraph. OFF by default
  // so manual scroll-to-preview isn't yanked back; toggled from the playback bar.
  // When off, the operator centers manually via the bar's ⊙ button. (#1564)
  autoCenter: boolean;
  // EPHEMERAL view-state (Phase 5): the composer-slot playback bar is collapsed
  // (hidden) while narration continues. Distinct from `autoCenter` — this is NOT
  // a persisted preference: it lives in the store only so it survives the
  // per-session composer remount ([[composer-per-session-remount]]), and is reset
  // to false on every startReading (a fresh read always shows its bar) and never
  // written to localStorage. Collapse is resolved at the slot level (MobileShell);
  // DocTtsPlaybackBar itself never reads this, so the CodeViewer doc bar is
  // unaffected. NOT an FSM phase — no TRANSITIONS row (R-A1 / SE-07 stay green).
  barCollapsed: boolean;
  // Internal: emit function injected by useSocket
  _emit: ((event: string, payload: unknown) => void) | null;
  // Internal: the single, gesture-unlocked HTMLAudioElement reused per segment
  _audio: HTMLAudioElement | null;
  // Internal: blob URL currently set on _audio (revoked on swap/stop)
  _currentUrl: string | null;
  // Internal: which segment _audio is currently bound to (ended/error → advance)
  _playingIndex: number | null;
  // Internal: armed via startReading() before segments existed — kick off on setSegments()
  _pendingStart: boolean;
}

// ─── FSM Transitions (R-A1) ─────────────────────────────────────────────────

export const TRANSITIONS = [
  { from: 'idle',    to: 'loading',  trigger: 'start' },
  { from: 'loading', to: 'playing',  trigger: 'audio_ready' },
  { from: 'playing', to: 'paused',   trigger: 'pause' },
  { from: 'paused',  to: 'playing',  trigger: 'resume' },
  { from: 'playing', to: 'loading',  trigger: 'seek' },
  { from: 'paused',  to: 'loading',  trigger: 'seek' },
  { from: 'loading', to: 'loading',  trigger: 'seek' },
  { from: 'playing', to: 'idle',     trigger: 'stop' },
  { from: 'paused',  to: 'idle',     trigger: 'stop' },
  { from: 'loading', to: 'idle',     trigger: 'stop' },
  { from: 'playing', to: 'idle',     trigger: 'complete' },
] as const;

// A 0.05s silent mono WAV (44-byte header + a few samples), base64-encoded.
// Used only to "bless" the shared audio element inside the click gesture so a
// later programmatic play() (after the WS round-trip) is permitted on mobile.
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * Identity comparison for two TtsSources. Used by startReading() to decide
 * whether the incoming read targets the SAME source as the last one (fast-path:
 * reuse already-annotated segments) or a DIFFERENT source (must clear stale
 * segments + re-arm so the new MarkdownContent annotation re-kicks).
 *
 * Without this, reading message B right after message A would synthesize A's
 * stale paragraph text under B's highlight (the segments array is never cleared
 * on stop/complete), because startReading's fast-path fires _requestSegment
 * against whatever segments happen to be in the store. The same latent bug
 * exists doc→doc; fixing it here hardens both paths.
 */
function sameTtsSource(a: TtsSource | null, b: TtsSource | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'doc' && b.kind === 'doc') return a.fileId === b.fileId;
  if (a.kind === 'chatMessage' && b.kind === 'chatMessage') {
    return a.sessionId === b.sessionId && a.messageId === b.messageId;
  }
  return false;
}

// ─── Store ──────────────────────────────────────────────────────────────────

interface DocTtsStore extends DocTtsState {
  // Actions (R-A2 — named, intent-reflecting)
  setSegments: (segments: TtsSegment[]) => void;
  setEmit: (emit: (event: string, payload: unknown) => void) => void;
  unlockAudio: () => void;
  startReading: (source: TtsSource) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  seekTo: (segmentIndex: number) => void;
  onAudioReceived: (audio: QueuedAudio) => void;
  onSegmentEnded: (index: number) => void;
  onSegmentError: (index: number) => void;
  setPlaybackRate: (rate: 1 | 1.5 | 2) => void;
  /** Step to the next speed (1 → 1.5 → 2 → 1), mirroring the chat playback pill's cycleSpeed. */
  cyclePlaybackRate: () => void;
  setAutoCenter: (v: boolean) => void;
  /** Collapse / re-show the composer-slot playback bar without stopping narration
   *  (Phase 5). Ephemeral — not persisted; reset on startReading. */
  setBarCollapsed: (v: boolean) => void;
  onComplete: () => void;
  // Server reconcile (R-A3 pattern)
  reconcile: (phase: DocTtsPhase) => void;
  // Internal helpers (prefixed _ to signal non-public)
  _requestSegment: (index: number) => void;
  _ensureAudioEl: () => HTMLAudioElement;
  _playSegment: (audio: QueuedAudio) => void;
  _stopCurrentAudio: () => void;
}

const initialState: DocTtsState = {
  phase: 'idle',
  source: null,
  segments: [],
  currentSegment: 0,
  pendingAudio: new Map<number, QueuedAudio>(),
  playbackRate: 1,
  autoCenter: (() => {
    try { return localStorage.getItem('va-reader-autocenter') === '1'; } catch { return false; }
  })(),
  barCollapsed: false,
  _emit: null,
  _audio: null,
  _currentUrl: null,
  _playingIndex: null,
  _pendingStart: false,
};

export const useDocTtsStore = create<DocTtsStore>()((set, get) => ({
  ...initialState,

  setSegments: (segments) => {
    set({ segments });
    // Arm-then-start: if startReading() armed us before segments existed, kick
    // off the first synth request now that MarkdownContent has annotated.
    if (get()._pendingStart && segments.length > 0) {
      set({ _pendingStart: false });
      const { currentSegment } = get();
      get()._requestSegment(currentSegment);
      get()._requestSegment(currentSegment + 1); // lookahead
    }
  },

  setEmit: (emit) => set({ _emit: emit }),

  /**
   * Unlock the shared audio element inside a user gesture (the Read-aloud
   * click). Plays a silent buffer muted so a later programmatic play() (after
   * the WS round-trip, outside the gesture) is permitted on iOS/Android.
   * Idempotent and safe to call on every click.
   */
  unlockAudio: () => {
    const el = get()._ensureAudioEl();
    try {
      el.muted = true;
      el.src = SILENT_WAV_DATA_URI;
      const p = el.play();
      const settle = () => { try { el.pause(); el.currentTime = 0; } catch { /* noop */ } el.muted = false; };
      if (p && typeof (p as Promise<void>).then === 'function') {
        (p as Promise<void>).then(settle).catch(() => { el.muted = false; });
      } else {
        settle();
      }
    } catch {
      /* priming is best-effort */
    }
  },

  startReading: (source) => {
    const { _emit, segments, source: prevSource } = get();
    if (!_emit) return;
    get()._stopCurrentAudio();

    // Source-swap guard: only reuse the existing `segments` when the incoming
    // read targets the SAME source as the last one (e.g. re-reading the same
    // open markdown file). For a DIFFERENT source (a different chat message, or
    // swapping file→message), the current `segments` belong to the previous
    // source — reusing them would synthesize the wrong text under the new
    // highlight. Clear them and re-arm so the new MarkdownContent's annotation
    // (→ setSegments) re-kicks the first synth request. This is modelled
    // entirely through the existing stop→start path; no new TRANSITIONS row.
    const reuseSegments = sameTtsSource(prevSource, source) && segments.length > 0;

    // Arm the machine. Flipping phase off 'idle' turns ttsMode on in the active
    // renderer (CodeViewer for docs / ChatArea for chat messages), which makes
    // MarkdownContent annotate + call setSegments(). Do NOT bail on empty
    // segments — that was the deadlock.
    set({
      phase: 'loading',
      source,
      currentSegment: 0,
      pendingAudio: new Map<number, QueuedAudio>(),
      // Drop the previous source's segments unless we're re-reading it. Keeping
      // them on a swap is the stale-audio bug.
      segments: reuseSegments ? segments : [],
      _pendingStart: !reuseSegments,
      // A fresh read always shows its bar — never inherit a collapse left over
      // from a previous read (Phase 5; ephemeral barCollapsed contract).
      barCollapsed: false,
    });
    if (reuseSegments) {
      // Segments already known for THIS source — go now.
      get()._requestSegment(0);
      get()._requestSegment(1); // lookahead
    }
    // else: setSegments() will fire the first request once annotation completes.
  },

  pause: () => {
    const { phase, _audio } = get();
    if (phase !== 'playing') return;
    _audio?.pause();
    set({ phase: 'paused' });
  },

  resume: () => {
    const { phase, currentSegment, _audio, pendingAudio } = get();
    if (phase !== 'paused') return;
    if (_audio && _audio.paused && _audio.src && get()._playingIndex != null) {
      _audio.play().catch(() => {});
      set({ phase: 'playing' });
    } else {
      // Audio element gone / no source — re-request from current segment
      set({ phase: 'loading' });
      const cached = pendingAudio.get(currentSegment);
      if (cached) {
        get()._playSegment(cached);
      } else {
        get()._requestSegment(currentSegment);
      }
    }
  },

  stop: () => {
    get()._stopCurrentAudio();
    // NOTE: `source` is intentionally NOT cleared here. Every consumer of `source`
    // (ChatArea.isChatReadActiveForMsg / isChatReadActive / the per-message ttsMode
    // prop) is already phase-aware (`phase !== idle && !== stopped`), so phase→idle
    // alone drops ttsMode + unmounts the bar. The DOM teardown (stripping stale
    // data-tts-segment/.tts-active) is FIX B and lives in MarkdownContent's
    // ttsMode-teardown effect. Retaining `source` here preserves the doc-viewer
    // same-source reuse fast-path in startReading (sameTtsSource(prevSource, …)).
    set({ phase: 'idle', currentSegment: 0, pendingAudio: new Map<number, QueuedAudio>(), _pendingStart: false });
  },

  seekTo: (segmentIndex) => {
    const { segments } = get();
    if (segmentIndex < 0 || segmentIndex >= segments.length) return;
    get()._stopCurrentAudio();
    set({ phase: 'loading', currentSegment: segmentIndex, pendingAudio: new Map<number, QueuedAudio>() });
    get()._requestSegment(segmentIndex);
    get()._requestSegment(segmentIndex + 1);
  },

  onAudioReceived: (audio) => {
    const { currentSegment, phase } = get();
    // Stale check: discard audio for segments behind current position
    if (audio.index < currentSegment) return;

    const newPending = new Map<number, QueuedAudio>(get().pendingAudio);
    newPending.set(audio.index, audio);
    set({ pendingAudio: newPending });

    // If this is the segment we're waiting for and we're in loading state, play it
    if (audio.index === currentSegment && phase === 'loading') {
      get()._playSegment(audio);
    }
  },

  onSegmentEnded: (index) => {
    const { segments, pendingAudio, phase } = get();
    if (phase !== 'playing' && phase !== 'loading') return;
    const nextIndex = index + 1;

    if (nextIndex >= segments.length) {
      get().onComplete();
      return;
    }

    // Skip code blocks
    const nextSeg = segments[nextIndex];
    if (nextSeg?.skip) {
      set({ currentSegment: nextIndex });
      get().onSegmentEnded(nextIndex);
      return;
    }

    set({ currentSegment: nextIndex, phase: 'loading' });
    const cached = pendingAudio.get(nextIndex);
    if (cached) {
      get()._playSegment(cached);
    } else {
      get()._requestSegment(nextIndex);
    }
    // Lookahead: prefetch nextIndex + 1
    get()._requestSegment(nextIndex + 1);
  },

  /**
   * A segment failed to synthesize (TTS_SEGMENT_ERROR). Don't hang the spinner —
   * advance past it as if it had played, so one bad segment can't freeze the
   * whole read. Only acts on the segment we're actually waiting for.
   */
  onSegmentError: (index) => {
    const { currentSegment, phase } = get();
    if (phase !== 'loading') return;
    if (index !== currentSegment) return; // a lookahead segment failed — ignore, we'll retry/skip when we reach it
    get().onSegmentEnded(index);
  },

  setPlaybackRate: (rate) => {
    const { _audio } = get();
    if (_audio) _audio.playbackRate = rate;
    set({ playbackRate: rate });
  },

  cyclePlaybackRate: () => {
    get().setPlaybackRate(nextPlaybackRate(get().playbackRate));
  },

  setAutoCenter: (v) => {
    try { localStorage.setItem('va-reader-autocenter', v ? '1' : '0'); } catch { /* ignore */ }
    set({ autoCenter: v });
  },

  // Ephemeral — deliberately NOT persisted (contrast setAutoCenter). A stale
  // collapse must never outlive the read it belongs to (it would hide the next
  // read's bar / the doc bar). startReading resets it; this only flips it.
  setBarCollapsed: (v) => set({ barCollapsed: v }),

  onComplete: () => {
    get()._stopCurrentAudio();
    // Same as stop(): phase→idle is sufficient to drop ttsMode (all consumers are
    // phase-aware) and `source` is retained for the doc same-source reuse fast-path.
    // FIX B's DOM cleanup is MarkdownContent's ttsMode-teardown effect.
    set({ phase: 'idle', currentSegment: 0, pendingAudio: new Map<number, QueuedAudio>(), _pendingStart: false });
  },

  reconcile: (phase) => {
    if (get().phase !== phase) set({ phase });
  },

  // ── Internal helpers ──────────────────────────────────────────────────────

  _requestSegment: (index: number) => {
    const { _emit, segments } = get();
    if (!_emit) return;
    const seg = segments[index];
    if (!seg || seg.skip || !seg.ttsText.trim()) return;
    if (get().pendingAudio.has(index)) return; // already fetching / cached
    _emit('tts_doc_synth', { text: seg.ttsText, index });
  },

  /**
   * Lazily create the single reusable audio element. ended/error listeners are
   * attached ONCE and dispatch against the live _playingIndex (the element is
   * reused across segments, so per-play handlers would leak).
   */
  _ensureAudioEl: () => {
    let el = get()._audio;
    if (el) return el;
    el = new Audio();
    el.addEventListener('ended', () => {
      const idx = get()._playingIndex;
      if (idx != null) get().onSegmentEnded(idx);
    });
    el.addEventListener('error', () => {
      const idx = get()._playingIndex;
      if (idx != null) get().onSegmentEnded(idx);
    });
    set({ _audio: el });
    return el;
  },

  _playSegment: (audio: QueuedAudio) => {
    const playbackRate = get().playbackRate;
    try {
      const bytes = Uint8Array.from(atob(audio.audioBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      // Revoke the previous segment's URL before swapping.
      const prevUrl = get()._currentUrl;
      if (prevUrl) URL.revokeObjectURL(prevUrl);

      const el = get()._ensureAudioEl();
      el.muted = false;
      el.src = url;
      el.playbackRate = playbackRate;
      set({ _audio: el, _currentUrl: url, _playingIndex: audio.index, phase: 'playing' });
      el.play().catch(() => {
        /* Autoplay blocked (no prior gesture-unlock) — surface as a stalled
           load rather than a hard error; the bar shows play to retry. */
      });
    } catch {
      get().onSegmentEnded(audio.index);
    }
  },

  _stopCurrentAudio: () => {
    const { _audio, _currentUrl } = get();
    if (_audio) {
      _audio.pause();
      // Keep the element (it stays gesture-unlocked for the next read); just
      // detach its current source.
      try { _audio.removeAttribute('src'); _audio.load(); } catch { /* noop */ }
    }
    if (_currentUrl) URL.revokeObjectURL(_currentUrl);
    set({ _currentUrl: null, _playingIndex: null });
  },
} as DocTtsStore));

// ─── Wire entry points (R-A2, platform-isolation #1657 Phase 2a) ──────────────
// The TTS_SEGMENT_AUDIO / TTS_SEGMENT_ERROR socket events are routed STRAIGHT into
// the named actions `onAudioReceived` / `onSegmentError` from useSocket.ts
// (`useDocTtsStore.getState().onAudioReceived(data)`), NOT through a window
// CustomEvent bridge. The store core is therefore environment-free — zero `window.`
// references — and the wire trigger is a named store action (R-A2). The former
// module-scope `window.addEventListener('va:tts-doc-*')` bridge was removed here.

// ─── Selector hook ──────────────────────────────────────────────────────────

export function useDocTtsEntry() {
  return useDocTtsStore(useShallow((s) => ({
    phase: s.phase,
    source: s.source,
    segments: s.segments,
    currentSegment: s.currentSegment,
    playbackRate: s.playbackRate,
    autoCenter: s.autoCenter,
    setAutoCenter: s.setAutoCenter,
    unlockAudio: s.unlockAudio,
    startReading: s.startReading,
    pause: s.pause,
    resume: s.resume,
    stop: s.stop,
    seekTo: s.seekTo,
    setPlaybackRate: s.setPlaybackRate,
    cyclePlaybackRate: s.cyclePlaybackRate,
  })));
}

/**
 * Composer-slot selector (Phase 5). Reports whether a CHAT-MESSAGE read is active
 * for the given session and whether its slot bar is collapsed — the two inputs the
 * shell needs to decide the composer pill slot occupancy. Returns a stable shallow
 * object. `active` mirrors ChatArea.isChatReadActive (phase-aware, session-scoped);
 * doc reads (`source.kind==='doc'`) deliberately return active=false so they never
 * claim the chat composer slot.
 */
export function useChatTtsSlot(sessionId: string | null | undefined) {
  return useDocTtsStore(useShallow((s) => ({
    active:
      s.source?.kind === 'chatMessage' &&
      s.source.sessionId === (sessionId ?? '') &&
      s.phase !== 'idle' &&
      s.phase !== 'stopped',
    collapsed: s.barCollapsed,
  })));
}
